import { useNavbar } from '@app/atoms/app/navbar.atom.ts';
import { useEffect, useState } from 'react';
import { Button, Tabs, Tab, BreadcrumbItem, Breadcrumbs } from '@heroui/react';
import { ArrowLeft2, Home2, Link } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import { LazyImage } from '@ui/common/lazy-image.tsx';
import { motion } from 'framer-motion';
import { useProduct } from '../../entites/product/use-product.ts';
import { ProductAction } from '@ui/product/product-action.tsx';
import { ProductFavorite } from '@ui/product/product-favorite.tsx';
import { ProductStores } from '@features/store/all-stores-list/product-stores.tsx';
import { F } from '@shared/utils/format.ts';

export const ProductPage = () => {
  const set = useNavbar();
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const { data, rawData } = useProduct({ productId: Number(productId) });
  const [selectedPhoto, setSelectedPhoto] = useState<string | undefined>();

  useEffect(() => {
    set.none();
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div
        className={`flex h-[56px] gap-x-4
          fixed top-0 left-0 right-0 z-10 px-4 justify-between
            items-center -ml-4 lg:hidden`}
      >
        <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
          <ArrowLeft2 size="24" color="#9CA5AC" />
        </Button>

        <div className="flex flex-row items-center gap-x-2 -mr-4">
          <ProductFavorite
            size="lg"
            isAbsolute={false}
            isSelected={data.isFavoriteSelected}
            onPress={data.onPressFavorite}
          />

          <Button isIconOnly variant="light" onPress={data.onPressShare}>
            <Link size="24" color="#9CA5AC" />
          </Button>
        </div>
      </div>

      <div className="hidden lg:block container max-w-7xl mx-auto mt-12">
        <Breadcrumbs>
          <BreadcrumbItem href="/">
            <Home2 size="24" color="#9CA5AC" />
          </BreadcrumbItem>
          <BreadcrumbItem color="primary" href="/">
            {rawData?.category_gender || ''}
          </BreadcrumbItem>
          <BreadcrumbItem
            color="primary"
            href={`/category/6/${rawData?.category_id}?ct=${rawData?.category_gender}&st=${rawData?.category_name}`}
          >
            {rawData?.category_name || ''}
          </BreadcrumbItem>
          <BreadcrumbItem color="primary">{data.title}</BreadcrumbItem>
        </Breadcrumbs>
      </div>

      <div className="hidden lg:flex gap-x-40 flex-row mt-12 container max-w-7xl mx-auto pb-28">
        <div className="flex flex-col w-[640px]">
          <div className="flex flex-row gap-y-4">
            <div className="w-[520px] h-[520px] aspect-square">
              <LazyImage src={selectedPhoto || data.image} />
            </div>

            <div className="flex flex-col gap-y-3">
              {data.photos.map((photo, index) => (
                <div
                  key={index}
                  onClick={() => {
                    if (photo.image) setSelectedPhoto(photo.image);
                  }}
                  className={`w-[88px] cursor-pointer h-[88px] aspect-square border-2 rounded-xl overflow-hidden ${photo.image === selectedPhoto ? 'border-[#1A73E8]' : 'border-transparent'}`}
                >
                  <LazyImage src={photo.image || undefined} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <p className="text-[#222222] font-semibold font-2xl">Описание</p>
            <p className="text-[#222222] mt-6">{data.description}</p>
          </div>
        </div>

        <div className="w-[550px] flex flex-col">
          <p className="text-[#222222] text-[28px] font-bold mt-3">
            {data.title}
          </p>

          <p className="text-[#9CA5AC] text-lg mt-2">{data.subtitle}</p>

          <p className="text-[#9CA5AC] text-base mt-3">{data.article}</p>

          <p className="text-[#222222] text-[32px] font-bold mt-4">
            {F.number(data.price)} ₸
          </p>

          <div className="flex flex-row mt-9 w-full items-center gap-x-4">
            <ProductAction
              size="lg"
              isLoading={data.isLoading}
              isSelected={data.isSelected}
              onPress={data.onPressAction}
            />

            <ProductFavorite
              size="lg"
              isAbsolute={false}
              isSelected={data.isFavoriteSelected}
              onPress={data.onPressFavorite}
            />
          </div>

          <div className="flex-col mt-9 p-4 border rounded-lg hidden">
            <p className="text-[#222222] text-lg font-semibold">Где купить?</p>

            <div className="pb-4">
              <ProductStores productId={Number(productId)} />
            </div>
          </div>
        </div>
      </div>

      <div className="-mx-4 pt-14 aspect-square lg:hidden">
        <LazyImage src={data.image} />
      </div>

      <div className="py-4 lg:hidden">
        <p className="text-[#222222] text-xl font-semibold mt-3">
          {data.title}
        </p>

        <p className="text-[#9CA5AC] text-sm mt-2">{data.subtitle}</p>

        <p className="text-[#9CA5AC] text-xs mt-3">{data.article}</p>

        <p className="text-[#222222] text-2xl font-semibold mt-4">
          {F.number(data.price)} ₸
        </p>
      </div>

      <div className="w-screen -mx-4 h-2 bg-[#F8F9FA] lg:hidden"></div>

      <Tabs
        aria-label="Options"
        className="bg-[#F5F5F5] rounded-lg mt-4 lg:hidden"
        fullWidth
        size="lg"
      >
        <Tab key="about" title="О товаре" className="lg:hidden">
          <p className="text-[#222222] text-lg font-semibold mt-3">Описание</p>
          <motion.div
            initial={{ height: 56 }}
            animate={{ height: isExpand ? '100%' : 56 }}
            exit={{ height: 5560 }}
            className="overflow-ellipsis overflow-hidden text-[#60656C] text-sm mt-3"
          >
            {data.description}
          </motion.div>
          <Button
            disableAnimation
            className="-ml-4 mt-3"
            onPress={() => setIsExpand(!isExpand)}
            variant="light"
            color="primary"
          >
            {isExpand ? 'Скрыть' : 'Показать полностью'}
          </Button>
        </Tab>

        <Tab key="address" title="Где купить?" className="lg:hidden">
          <p className="text-[#222222] text-lg font-semibold mt-3">
            Аптеки Zerde
          </p>

          <div className="mt-3">
            <ProductStores productId={Number(productId)} />
          </div>
        </Tab>
      </Tabs>

      <div className="h-16 lg:hidden" />

      <div
        className={`drop-shadow-[0_0_12px_#34578C26] h-20 p-4 bg-white fixed bottom-0 left-0 right-0 z-30 lg:hidden`}
      >
        <ProductAction
          size="lg"
          isLoading={data.isLoading}
          isSelected={data.isSelected}
          onPress={data.onPressAction}
        />
      </div>
    </>
  );
};
